import React, {
  memo,
  useMemo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import InfoBubble from '@gp/components/InfoBubble';
import GrossRevenueCard from './components/GrossRevenueCard';
import CommonContainer from '../components/CommonContainer';
import { useRequest } from 'ahooks';
import {
  fetchStudioGrossRevenueData,
  studioGrossRevenueDownLoad,
  fetchProjectGrossRevenueData,
  projectGrossRevenueDownLoad,
} from './services';
import {
  fetchStudioFilterData,
  fetchProjectFilterData,
} from '@gp/pages/StudioMonthlyReport/PerformanceOverview/services';
import { reportOperationLog } from '@gp/services/global';
import { ALL_REPORT_OPERATION_LOG } from '@gp/constants/operationLogParams';
import {
  getArraySort,
  toFirstUpperCase,
  isValidNumber,
} from '@gp/utils/common';
import { getTableRecords, getTableOptions } from './utils';
import moment from 'moment';
import { SortBy } from 'tea-component/lib/table/addons';
import { IFilter } from '@gp/pages/StudioDashboard/StudioOverall/Tabs/People/Details/components/Filter';
import { TableListOptionsType } from '@gp/components/GeneralTable/index.d';
import { GrossRevenueDataType } from './types/services';
import './index.scss';
import TipContext from '@/pages/home/components/TipContext';
import { CONSTANT_TEXTS } from '@gp/constants/const';

const GrossRevenueTrends: React.FC<unknown> = () => {
  // 头部显示的时间
  const [headerDate, setHeaderDate] = useState<string>('');
  // 控制头部时间只取一次
  const isFinishHeaderDate = useRef<boolean>(false);
  const [studioFilterTimeRange, setStudioFilterTimeRange] = useState<{
    startDate: string;
    endDate: string;
  } | null>(null);
  const [projectFilterTimeRange, setProjectFilterTimeRange] = useState<{
    startDate: string;
    endDate: string;
  } | null>(null);
  // 通知Project筛选器刷新下拉项及重新全选
  const [
    projectGrossRevenueProjectFilterRefreshRenderKey,
    setProjectGrossRevenueProjectFilterRefreshRenderKey,
  ] = useState<number>(0);
  // studio全选时project筛选器的下拉项
  const projectGrossRevenueAllProjectFilterOptionsRef = useRef<
    {
      value: string;
      text: string;
      label: string;
      groupKey: string;
    }[]
  >([]);
  const [
    projectGrossRevenueProjectFilterOptions,
    setProjectGrossRevenueProjectFilterOptions,
  ] = useState<
    {
      value: string;
      text: string;
      label: string;
      groupKey: string;
    }[]
  >([]);
  const studioGrossRevenueParamsListRef = useRef<{
    select_id: string[];
    start_date: string;
    end_date: string;
    yoy: boolean;
    mom: boolean;
    order: 'desc' | 'asc' | undefined;
    order_metric: string;
  }>({
    select_id: [],
    start_date: '',
    end_date: '',
    yoy: true,
    mom: false,
    order: 'desc',
    // 这个默认传空，后端会按最新月份降序排序，所以接口可以同时调用
    order_metric: '',
  });
  const projectGrossRevenueParamsListRef = useRef<{
    select_id: string[];
    start_date: string;
    end_date: string;
    yoy: boolean;
    mom: boolean;
    order: 'desc' | 'asc' | undefined;
    order_metric: string;
  }>({
    select_id: [],
    start_date: '',
    end_date: '',
    yoy: true,
    mom: false,
    order: 'desc',
    // 这个默认传空，后端会按最新月份降序排序，所以接口可以同时调用
    order_metric: '',
  });
  const projectGrossRevenueStudioFilterOptionsRef = useRef<
    {
      value: string;
      text: string;
      projects: {
        id: string;
        name: string;
      }[];
      [keyName: string]: any;
    }[]
  >([]);

  const {
    loading: studioGrossRevenueFilterLoading,
    data: studioGrossRevenueFilterData,
  } = useRequest(fetchStudioFilterData, {
    defaultParams: [
      {
        page: 'gross_revenue_trends',
      },
    ],
  });
  const {
    loading: studioGrossRevenueLoading,
    data: studioGrossRevenueData,
    run: studioGrossRevenueRun,
  } = useRequest(fetchStudioGrossRevenueData, { manual: true });
  const {
    loading: projectGrossRevenueFilterLoading,
    data: projectGrossRevenueFilterData,
  } = useRequest(fetchProjectFilterData, {
    defaultParams: [
      {
        page: 'gross_revenue_trends',
      },
    ],
  });
  const {
    loading: projectGrossRevenueLoading,
    data: projectGrossRevenueData,
    run: projectGrossRevenueRun,
  } = useRequest(fetchProjectGrossRevenueData, { manual: true });

  const studioGrossRevenueStudioFilterOptions = useMemo(() => {
    if (
      studioGrossRevenueFilterData?.start_date &&
      studioGrossRevenueFilterData?.end_date
    ) {
      setStudioFilterTimeRange({
        startDate: studioGrossRevenueFilterData?.start_date,
        endDate: studioGrossRevenueFilterData?.end_date,
      });
    }
    const studioList = studioGrossRevenueFilterData?.list;
    if (!studioList || !Array.isArray(studioList)) {
      return [];
    }
    const _options =
      getArraySort(studioList, 'string', 'asc', 'name')?.map(
        (t: { name: string; id: string }) => {
          return {
            value: t?.id || '',
            text: t?.name || '-',
            label: t?.name || '-',
            groupKey: 'separated',
          };
        },
      ) || [];

    return _options;
  }, [studioGrossRevenueFilterLoading]);

  const projectGrossRevenueStudioFilterOptions = useMemo(() => {
    if (
      projectGrossRevenueFilterData?.start_date &&
      projectGrossRevenueFilterData?.end_date
    ) {
      setProjectFilterTimeRange({
        startDate: projectGrossRevenueFilterData?.start_date,
        endDate: projectGrossRevenueFilterData?.end_date,
      });
    }
    const studioList = projectGrossRevenueFilterData?.list;
    let newProjectOptions: {
      id: string;
      name: string;
    }[] = [];
    if (!studioList || !Array.isArray(studioList)) {
      projectGrossRevenueStudioFilterOptionsRef.current = [];
      return [];
    }
    const _options =
      getArraySort(studioList, 'string', 'asc', 'name')?.map((t) => {
        const currentItemProjects = t?.projects || [];
        newProjectOptions = [...newProjectOptions, ...currentItemProjects];
        return {
          value: t?.id || '',
          text: t?.name || '-',
          label: t?.name || '-',
          projects: t?.projects,
          groupKey: 'separated',
        };
      }) || [];

    setProjectGrossRevenueProjectFilterRefreshRenderKey(Date.now());
    const allProjectOptions =
      getArraySort(newProjectOptions, 'string', 'asc', 'name')?.map(
        (t: { name: string; id: string }) => {
          return {
            value: t?.id || '',
            text: t?.name || '-',
            label: t?.name || '-',
            groupKey: 'separated',
          };
        },
      ) || [];
    setProjectGrossRevenueProjectFilterOptions(allProjectOptions);
    projectGrossRevenueAllProjectFilterOptionsRef.current = allProjectOptions;

    projectGrossRevenueStudioFilterOptionsRef.current = _options;
    return _options;
  }, [projectGrossRevenueFilterLoading]);

  const studioGrossRevenueFilterOptions = useMemo(() => {
    return [
      {
        type: 'multipleselect',
        fieldName: 'studio',
        value: [],
        option: studioGrossRevenueStudioFilterOptions,
        className: 'dbg-gross-revenue-card-filter-studio-selector',
        style: { marginRight: 10 },
        listWidth: 271,
        label: 'Studio',
        disabled: studioGrossRevenueFilterLoading,
        placeholder: 'Please select Studio',
      },
    ];
  }, [studioGrossRevenueFilterLoading, studioGrossRevenueStudioFilterOptions]);

  const projectGrossRevenueFilterOptions = useMemo(() => {
    return [
      {
        refreshRenderKey: projectGrossRevenueProjectFilterRefreshRenderKey,
        type: 'multipleselect',
        fieldName: 'project',
        value: [],
        option: projectGrossRevenueProjectFilterOptions,
        className: 'dbg-gross-revenue-card-filter-project-selector',
        style: { marginRight: 10 },
        label: 'Project',
        disabled: projectGrossRevenueFilterLoading,
        placeholder: 'Please select Project',
      },
      {
        type: 'multipleselect',
        fieldName: 'studio',
        value: [],
        option: projectGrossRevenueStudioFilterOptions,
        className: 'dbg-gross-revenue-card-filter-studio-selector',
        style: { marginRight: 10 },
        listWidth: 271,
        label: 'Studio',
        disabled: projectGrossRevenueFilterLoading,
        placeholder: 'Please select Studio',
      },
    ];
  }, [
    projectGrossRevenueFilterLoading,
    projectGrossRevenueStudioFilterOptions,
    projectGrossRevenueProjectFilterOptions,
    projectGrossRevenueProjectFilterRefreshRenderKey,
  ]);

  const studioGrossRevenueTableOptions: TableListOptionsType = useMemo(() => {
    return getTableOptions(
      'studio',
      studioGrossRevenueLoading,
      studioGrossRevenueData as GrossRevenueDataType,
      studioGrossRevenueParamsListRef?.current?.yoy,
      studioGrossRevenueParamsListRef?.current?.mom,
    );
  }, [studioGrossRevenueLoading]);

  const projectGrossRevenueTableOptions: TableListOptionsType = useMemo(() => {
    return getTableOptions(
      'project',
      projectGrossRevenueLoading,
      projectGrossRevenueData as GrossRevenueDataType,
      projectGrossRevenueParamsListRef?.current?.yoy,
      projectGrossRevenueParamsListRef?.current?.mom,
    );
  }, [projectGrossRevenueLoading]);

  const studioGrossRevenueTableRecords = useMemo(() => {
    return getTableRecords(
      studioGrossRevenueLoading,
      studioGrossRevenueData as GrossRevenueDataType,
    );
  }, [studioGrossRevenueLoading]);

  const projectGrossRevenueTableRecords = useMemo(() => {
    return getTableRecords(
      projectGrossRevenueLoading,
      projectGrossRevenueData as GrossRevenueDataType,
    );
  }, [projectGrossRevenueLoading]);

  const handleStudioGrossRevenueTableSort = useCallback((v: SortBy<any>) => {
    studioGrossRevenueParamsListRef.current = {
      ...studioGrossRevenueParamsListRef.current,
      order: v?.order,
      order_metric: v?.by,
    };
    studioGrossRevenueRun(studioGrossRevenueParamsListRef.current);
  }, []);

  const handleProjectGrossRevenueTableSort = useCallback((v: SortBy<any>) => {
    projectGrossRevenueParamsListRef.current = {
      ...projectGrossRevenueParamsListRef.current,
      order: v?.order,
      order_metric: v?.by,
    };
    projectGrossRevenueRun(projectGrossRevenueParamsListRef.current);
  }, []);

  const handleStudioGrossRevenueFilterChange = useCallback(
    (
      studio: string[],
      project: string[],
      date: IFilter,
      ym: string[],
      handleFilterName: string,
    ) => {
      studioGrossRevenueParamsListRef.current = {
        ...studioGrossRevenueParamsListRef.current,
        select_id: studio,
        start_date: date?.start_date,
        end_date: date?.end_date,
        yoy: ym?.includes('1'),
        mom: ym?.includes('2'),
      };
      studioGrossRevenueRun(studioGrossRevenueParamsListRef.current);
    },
    [],
  );

  const handleProjectGrossRevenueFilterChange = useCallback(
    (
      studio: string[],
      project: string[],
      date: IFilter,
      ym: string[],
      handleFilterName: string,
    ) => {
      let selectId: string[] = [],
        newProjectOptions: {
          id: string;
          name: string;
        }[] = [];
      if (handleFilterName === 'project') {
        selectId = project;
        // studio筛选器变化时需要改变project筛选器的筛选项，并且project筛选器要重新全选
      } else if (handleFilterName === 'studio') {
        // 全选
        if (!studio?.length) {
          setProjectGrossRevenueProjectFilterRefreshRenderKey(Date.now());
          setProjectGrossRevenueProjectFilterOptions(
            projectGrossRevenueAllProjectFilterOptionsRef.current,
          );
        } else {
          for (let i = 0, l = studio?.length; i < l; i++) {
            const t = studio?.[i];
            const projectPerformanceStudioFilterOptions =
              projectGrossRevenueStudioFilterOptionsRef.current;
            if (Array.isArray(projectPerformanceStudioFilterOptions)) {
              for (
                let j = 0, l = projectPerformanceStudioFilterOptions?.length;
                j < l;
                j++
              ) {
                const k = projectPerformanceStudioFilterOptions?.[j];
                if (k?.value === t && Array.isArray(k?.projects)) {
                  const currentItemProjects = k?.projects || [];
                  const currentItemProjectIds =
                    currentItemProjects?.map((item) => item?.id) || [];
                  newProjectOptions = [
                    ...newProjectOptions,
                    ...currentItemProjects,
                  ];
                  selectId = [...selectId, ...currentItemProjectIds];
                }
              }
            }
            if (i === l - 1) {
              setProjectGrossRevenueProjectFilterRefreshRenderKey(Date.now());
              setProjectGrossRevenueProjectFilterOptions(
                getArraySort(newProjectOptions, 'string', 'asc', 'name')?.map(
                  (t: { name: string; id: string }) => {
                    return {
                      value: t?.id || '',
                      text: t?.name || '-',
                      label: t?.name || '-',
                      groupKey: 'separated',
                    };
                  },
                ) || [],
              );
            }
          }
        }
      } else {
        selectId = projectGrossRevenueParamsListRef?.current?.select_id;
      }
      projectGrossRevenueParamsListRef.current = {
        ...projectGrossRevenueParamsListRef.current,
        select_id: selectId,
        start_date: date?.start_date,
        end_date: date?.end_date,
        yoy: ym?.includes('1'),
        mom: ym?.includes('2'),
      };
      projectGrossRevenueRun(projectGrossRevenueParamsListRef.current);
    },
    [],
  );

  // 初始化请求数据
  useEffect(() => {
    studioGrossRevenueRun(studioGrossRevenueParamsListRef.current);
    projectGrossRevenueRun(projectGrossRevenueParamsListRef.current);
    // 页面打点
    reportOperationLog(
      ALL_REPORT_OPERATION_LOG['STUDIO_MONTHLY_REPORT_GROSS_REVENUE_TRENDS'],
    );
  }, []);

  // 组织头部显示的时间
  useEffect(() => {
    if (
      isFinishHeaderDate.current ||
      studioGrossRevenueLoading ||
      projectGrossRevenueLoading
    )
      return;
    const studioPerformanceDate = studioGrossRevenueData?.date_time || '',
      projectPerformanceDate = projectGrossRevenueData?.date_time || '';
    if (!studioGrossRevenueData && !projectGrossRevenueData) return;
    const maxDate = moment.max(
      moment(studioPerformanceDate || '1970-01', 'YYYY-MM'),
      moment(projectPerformanceDate || '1970-01', 'YYYY-MM'),
    );
    const resultDate = maxDate.isValid() ? maxDate.format('YYYY-MM') : '';
    setHeaderDate(resultDate);
    isFinishHeaderDate.current = true;
  }, [studioGrossRevenueLoading, projectGrossRevenueLoading]);

  return (
    <CommonContainer
      loading={false}
      title={'Gross Revenue Trends'}
      subTitle={`Latest Data: ${headerDate || '-'}`}
    >
      <div className="dbg-studio-monthly-report-gross-revenue">
        {/* Studio Gross Revenue */}
        <GrossRevenueCard
          title={
            <div className="dbg-studio-monthly-report-card-title">
              Studio Gross Revenue
              <TipContext
                AWidth={555}
                placement="right"
                className="globalpedia-modal-font14 dbg-studio-monthly-report-card-title-info"
                tipText={
                  <>
                    <p>【Metrics】: Actual (Revenue & Profit)</p>
                    <p>{CONSTANT_TEXTS.DATA_SOURCE}: Reporting Package</p>
                    <p>{CONSTANT_TEXTS.UPDATE_FREQUENCY}: Monthly</p>
                  </>
                }
              />
            </div>
          }
          filterLoading={studioGrossRevenueFilterLoading}
          filterTimeRangeData={studioFilterTimeRange}
          filterOptions={studioGrossRevenueFilterOptions}
          filterChange={handleStudioGrossRevenueFilterChange}
          tableClassName="dbg-studio-monthly-report-gross-revenue-studio-list dbg-table-sticky"
          tableLoading={studioGrossRevenueLoading}
          tableOptions={studioGrossRevenueTableOptions}
          tableRecords={studioGrossRevenueTableRecords}
          onTableSort={handleStudioGrossRevenueTableSort}
          uniqueDownloadId="studio_monthly_report_gross_revenue_studio_gross_revenue_download"
          downloadParams={studioGrossRevenueParamsListRef.current}
          downloadServices={studioGrossRevenueDownLoad}
          downloadLogKey={{
            csv: 'STUDIO_MONTHLY_REPORT_GROSS_REVENUE_TRENDS_STUDIO_GROSS_REVENUE_CSV_DOWNLOAD_SUBMIT',
            excel:
              'STUDIO_MONTHLY_REPORT_GROSS_REVENUE_TRENDS_STUDIO_GROSS_REVENUE_EXCEL_DOWNLOAD_SUBMIT',
          }}
        />
        {/* Project Gross Revenue */}
        <GrossRevenueCard
          title={
            <div className="dbg-studio-monthly-report-card-title">
              Project Gross Revenue
              <TipContext
                AWidth={555}
                placement="right"
                className="globalpedia-modal-font14 dbg-studio-monthly-report-card-title-info"
                tipText={
                  <>
                    <p>【Metrics】: Actual (Revenue & Profit)</p>
                    <p>{CONSTANT_TEXTS.DATA_SOURCE}: Reporting Package</p>
                    <p>{CONSTANT_TEXTS.UPDATE_FREQUENCY}: Monthly</p>
                  </>
                }
              />
            </div>
          }
          filterLoading={projectGrossRevenueFilterLoading}
          filterTimeRangeData={projectFilterTimeRange}
          filterOptions={projectGrossRevenueFilterOptions}
          filterChange={handleProjectGrossRevenueFilterChange}
          tableClassName="dbg-studio-monthly-report-gross-revenue-project-list dbg-table-sticky"
          tableLoading={projectGrossRevenueLoading}
          tableOptions={projectGrossRevenueTableOptions}
          tableRecords={projectGrossRevenueTableRecords}
          onTableSort={handleProjectGrossRevenueTableSort}
          uniqueDownloadId="studio_monthly_report_gross_revenue_project_gross_revenue_download"
          downloadParams={projectGrossRevenueParamsListRef.current}
          downloadServices={projectGrossRevenueDownLoad}
          downloadLogKey={{
            csv: 'STUDIO_MONTHLY_REPORT_GROSS_REVENUE_TRENDS_PROJECT_GROSS_REVENUE_CSV_DOWNLOAD_SUBMIT',
            excel:
              'STUDIO_MONTHLY_REPORT_GROSS_REVENUE_TRENDS_PROJECT_GROSS_REVENUE_EXCEL_DOWNLOAD_SUBMIT',
          }}
        />
      </div>
    </CommonContainer>
  );
};

export default memo(GrossRevenueTrends);
